import * as React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { Title } from 'react-admin';
import { isAdmin } from '../../core/roles.ts';
import type { AdminProduct, AttractionRules, DurationMinutes } from '../../services/attractions/attractionsService.ts';

export { errorText } from '../../services/shabbat/shabbatService.ts';

export const COLORS = {
    page: '#F3F5F4',
    border: '#E5E7EB',
    rowBorder: '#F0F2F1',
    text: '#111827',
    muted: '#6B7280',
    body: '#374151',
    teal: '#24BBAA',
    tealDark: '#03676A',
    tealSoft: '#DDF4F1',
    warn: '#B45309',
};

/** Rules defaults when the server has none to offer (the README's values). */
export const DEFAULT_RULES: AttractionRules = {
    minReviews: 50,
    minRating: 4.3,
    hideNightlife: true,
    hideSaturdayOnly: true,
    hideMultiDay: false,
    multiDayHours: 12,
    groupDuplicates: true,
    hideTransfers: true,
};

const hours = (minutes: number): string => {
    const h = Math.round((minutes / 60) * 2) / 2;
    return Number.isInteger(h) ? String(h) : h.toFixed(1);
};

const oneDuration = (m: number): string => (m < 60 ? `${m} min` : `${hours(m)} h`);

/** "45 min", "4 h", "3–4 h", "Full day" (over 7 h), "3 days". */
export const fmtDuration = (d: DurationMinutes | null | undefined, text?: string | null): string => {
    if (!d || d.max == null) return text || '—';
    const min = d.min ?? d.max;
    const max = d.max;
    if (max >= 24 * 60) {
        const days = Math.round(max / (24 * 60));
        return days <= 1 ? 'Full day' : `${days} days`;
    }
    if (max > 7 * 60) return 'Full day';
    if (min === max || oneDuration(min) === oneDuration(max)) return oneDuration(max);
    if (min >= 60) return `${hours(min)}–${hours(max)} h`;
    return `${oneDuration(min)}–${oneDuration(max)}`;
};

/** "today", "yesterday", "3 days ago", "12 Oct". */
export const fmtSynced = (iso: string | null | undefined): string => {
    if (!iso) return 'never synced';
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return 'never synced';
    const day = (d: Date) => Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() / 86400000);
    const diff = day(new Date()) - day(then);
    if (diff <= 0) return 'last synced today';
    if (diff === 1) return 'last synced yesterday';
    if (diff < 7) return `last synced ${diff} days ago`;
    return `last synced ${then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
};

export const fmtDateTime = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const dayNumber = (d: Date) => Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000);

/**
 * A plan date (YYYY-MM-DD) and optional HH:mm start as "Today · 08:30",
 * "Tomorrow", "Tue 27 Oct · 08:30" or "Tue 27 Oct 2027". The date is the
 * attraction's own local day, so it is never shifted through a time zone.
 */
export const fmtPlanDate = (planDate: string, startTime?: string | null, now = new Date()): string => {
    const [y, m, d] = planDate.split('-').map(Number);
    if (!y || !m || !d) return planDate;
    const date = new Date(y, m - 1, d);
    const diff = dayNumber(date) - dayNumber(now);
    const day = diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : diff === -1 ? 'Yesterday'
        : date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', ...(y !== now.getFullYear() ? { year: 'numeric' } : {}) })
            .replace(',', '');
    return startTime ? `${day} · ${startTime.slice(0, 5)}` : day;
};

/** "just now", "5 min ago", "3 h ago", "yesterday", "4 days ago", then the date. */
export const fmtAgo = (iso: string | null | undefined, now = new Date()): string => {
    if (!iso) return '';
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return '';
    const minutes = Math.floor((now.getTime() - then.getTime()) / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min ago`;
    if (minutes < 24 * 60 && dayNumber(then) === dayNumber(now)) return `${Math.floor(minutes / 60)} h ago`;
    const days = dayNumber(now) - dayNumber(then);
    if (days <= 1) return minutes < 24 * 60 ? `${Math.floor(minutes / 60)} h ago` : 'yesterday';
    if (days < 7) return `${days} days ago`;
    return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(then.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
};

export const fmtCount = (n: number | null | undefined): string => (n ?? 0).toLocaleString('en-US');

/** Why an automatic rule removed a product, in the rules' own numbers. */
export const ruleText = (p: AdminProduct, rules: AttractionRules | null): string | null => {
    switch (p.ruleReason) {
        case 'REVIEWS': return `Hidden by rule: fewer than ${rules?.minReviews ?? 50} reviews`;
        case 'RATING': return `Hidden by rule: under ${rules?.minRating ?? 4.3} stars`;
        case 'NIGHTLIFE': return 'Hidden by rule: nightlife';
        case 'SATURDAY_ONLY': return 'Hidden by rule: Saturday-only';
        case 'MULTI_DAY': return `Hidden by rule: multi-day (over ${rules?.multiDayHours ?? 12} h)`;
        case 'TRANSFER': return 'Hidden by rule: transfer';
        case 'GONE': return 'No longer on Viator';
        default: return null;
    }
};

/** Passes the rules, or an admin overrode them (never for GONE). Same test as the backend's ruleCleared(). */
export const ruleCleared = (p: AdminProduct): boolean => !p.ruleReason || (!!p.ruleOverride && p.ruleReason !== 'GONE');

/** Can be overridden: a rule hides it, it is not overridden yet, and it is still on Viator. */
export const canOverride = (p: AdminProduct): boolean => !!p.ruleReason && p.ruleReason !== 'GONE' && !p.ruleOverride;

/** "Min reviews (32 < 50), Min rating (4.1 < 4.3)", from the backend's list, else from ruleReason. */
export const failedRules = (p: AdminProduct, rules: AttractionRules | null): string => {
    if (p.ruleFailures && p.ruleFailures.length > 0) return p.ruleFailures.join(', ');
    const text = ruleText(p, rules);
    return text ? text.replace(/^Hidden by rule: /, '') : '';
};

export const hiddenText = (p: AdminProduct): string | null => {
    switch (p.hiddenReason) {
        case 'DUPLICATE': return 'Hidden as a near-duplicate';
        case 'ADMIN': return 'Hidden by an admin';
        default: return null;
    }
};

export const Tile: React.FC<{ label: string; value: string; sub: string }> = ({ label, value, sub }) => (
    <Box sx={{ bgcolor: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: '12px', px: 2.25, py: 2 }}>
        <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>{label}</Typography>
        <Typography sx={{ fontSize: 28, fontWeight: 700, color: COLORS.text, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
        <Typography sx={{ fontSize: 12.5, color: COLORS.tealDark, mt: 0.25 }}>{sub}</Typography>
    </Box>
);

export const Panel: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx }) => (
    <Box sx={{ bgcolor: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: '12px', ...sx }}>{children}</Box>
);

/** A teal collection tag. */
export const CollectionChip: React.FC<{ label: string }> = ({ label }) => (
    <Box component="span" sx={{ fontSize: 11.5, color: COLORS.tealDark, bgcolor: COLORS.tealSoft, borderRadius: '9px', px: 1, py: '1px', whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
    </Box>
);

/**
 * The header every Attractions page shares (same frame as the Shabbat pages):
 * white bar with title, subtitle and the page's actions over the grey page.
 * Admins only; the routes are not even registered for anyone else, and the
 * backend enforces the same on every endpoint.
 */
export const AttractionsFrame: React.FC<{
    title: string;
    subtitle: string;
    actions?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, subtitle, actions, children }) => {
    if (!isAdmin()) {
        return (
            <>
                <Title title={title} />
                <Card sx={{ mt: 2, maxWidth: 640 }}>
                    <CardContent>
                        <Typography variant="body1">You do not have permission to view this page.</Typography>
                    </CardContent>
                </Card>
            </>
        );
    }
    return (
        <Box data-attractions-frame="1" sx={{ pb: 6, bgcolor: COLORS.page, minHeight: 'calc(100vh - 72px)', width: '100%', minWidth: 0, contain: 'inline-size' }}>
            <Title title={title} />
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.75, flexWrap: 'wrap',
                px: { xs: 2, md: 3.5 }, py: 1.5, bgcolor: '#fff', borderBottom: `1px solid ${COLORS.border}`,
            }}>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: COLORS.text }}>{title}</Typography>
                    <Typography sx={{ fontSize: 13, color: COLORS.muted }}>{subtitle}</Typography>
                </Box>
                {actions}
            </Box>
            <Box sx={{ px: { xs: 2, md: 3.5 }, pt: 3 }}>{children}</Box>
        </Box>
    );
};

export const pillButtonSx = {
    height: 40, borderRadius: '20px', border: `1px solid ${COLORS.border}`, bgcolor: '#fff', color: COLORS.text,
    fontWeight: 500, px: 2, '&:hover': { bgcolor: '#F9FAFB', borderColor: '#D1D5DB' },
};
